import { useMemo, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ArrowRight, CalendarDays, History, MessageSquareQuote, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";

import { PersonBadge } from "@/components/shared/PersonBadge";
import { TAREFA_LOG_EVENTO_CONFIG } from "./tarefa-log-look";
import { useTarefaHistorico } from "@/hooks/tarefas/use-tarefa-historico";
import { TarefaLogDTO } from "@/types/tarefa-log.type";

interface TarefaHistoricoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tarefaId: number;
  tarefaNome?: string;
}

const formatGroupDate = (dateString: string) => {
  const date = new Date(dateString);
  if (isToday(date)) return "Hoje";
  if (isYesterday(date)) return "Ontem";
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
};

/** `dados.prazo`/`dados.responsavel` já vêm no formato `{ label, valorAnterior, valorNovo }`. */
function extrairCampoAlterado(dados: TarefaLogDTO["dados"]) {
  if (!dados) return null;
  if ("prazo" in dados) return dados.prazo;
  if ("responsavel" in dados) return dados.responsavel;
  return null;
}

function extrairPreview(dados: TarefaLogDTO["dados"]) {
  if (dados && "preview" in dados) return dados.preview;
  return null;
}

const TarefaLogItem = ({ log }: { log: TarefaLogDTO }) => {
  const config = TAREFA_LOG_EVENTO_CONFIG[log.evento];
  const Icon = config.icon;
  const campoAlterado = extrairCampoAlterado(log.dados);
  const preview = extrairPreview(log.dados);

  return (
    <li className="relative">
      <span
        className={cn(
          "absolute -left-12 top-0 flex h-8 w-8 items-center justify-center rounded-full ring-4 ring-card",
          config.tone,
        )}
      >
        <Icon className="h-4 w-4" />
      </span>

      <div className="group rounded-lg border border-border/60 bg-card p-3.5 shadow-sm transition-colors hover:border-border">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <span className="text-sm font-semibold text-foreground">{config.label}</span>
          <span className="font-mono text-xs text-muted-foreground">
            {format(new Date(log.criadoEm), "HH:mm")}
          </span>

          {log.ator && (
            <PersonBadge pessoa={log.ator} isCompact format="short" className="ml-auto w-auto" />
          )}
        </div>

        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{log.descricao}</p>

        {campoAlterado && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-md border border-rose-100 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-300">
              {campoAlterado.valorAnterior ?? "Vazio"}
            </span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
            <span className="rounded-md border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300">
              {campoAlterado.valorNovo ?? "Vazio"}
            </span>
          </div>
        )}

        {preview && (
          <div className="mt-3 flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs italic text-muted-foreground">
            <MessageSquareQuote className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>&ldquo;{preview}&rdquo;</span>
          </div>
        )}
      </div>
    </li>
  );
};

export function TarefaHistoricoDialog({
  open,
  onOpenChange,
  tarefaId,
  tarefaNome,
}: TarefaHistoricoDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { logs, isLoading } = useTarefaHistorico(tarefaId, open);

  const filteredLogs = useMemo(() => {
    if (!searchTerm) return logs;
    const term = searchTerm.toLowerCase();
    return logs.filter(
      (log) =>
        log.descricao.toLowerCase().includes(term) ||
        log.ator?.nome?.toLowerCase().includes(term) ||
        TAREFA_LOG_EVENTO_CONFIG[log.evento].label.toLowerCase().includes(term),
    );
  }, [logs, searchTerm]);

  const groupedLogs = useMemo(() => {
    const groups: Record<string, TarefaLogDTO[]> = {};
    filteredLogs.forEach((log) => {
      const dateKey = format(new Date(log.criadoEm), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(log);
    });
    return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
  }, [filteredLogs]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[92vh] w-[95vw] flex-col gap-0 overflow-hidden border-border bg-card p-0 shadow-2xl sm:h-[85vh] sm:w-full sm:max-w-3xl sm:rounded-xl">
        <DialogHeader className="z-20 shrink-0 border-b border-border bg-card p-4 pb-3 sm:p-5 sm:pb-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex items-center gap-2.5 text-base text-foreground sm:text-lg">
                <div className="shrink-0 rounded-md bg-primary/10 p-1.5">
                  <History className="h-4 w-4 text-primary sm:h-5 sm:w-5" />
                </div>
                <span className="truncate">Histórico da tarefa</span>
              </DialogTitle>
              {tarefaNome && (
                <DialogDescription
                  className="mt-1.5 truncate text-xs text-muted-foreground sm:text-sm"
                  title={tarefaNome}
                >
                  {tarefaNome}
                </DialogDescription>
              )}
            </div>

            {!isLoading && (
              <Badge variant="secondary" className="mt-1 hidden shrink-0 sm:flex">
                {filteredLogs.length} eventos
              </Badge>
            )}
          </div>

          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por tipo, autor ou texto..."
              className="h-9 border-border bg-muted/30 pl-9 text-sm sm:h-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </DialogHeader>

        <div className="relative min-h-0 w-full flex-1 bg-card/50">
          <ScrollArea className="h-full w-full">
            <div className="p-4 sm:p-6">
              {isLoading ? (
                <div className="flex h-[40vh] animate-pulse flex-col items-center justify-center gap-3 text-muted-foreground">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary/30 border-t-primary sm:h-10 sm:w-10" />
                  <p className="text-[11px] font-medium sm:text-sm">Carregando histórico...</p>
                </div>
              ) : filteredLogs.length === 0 ? (
                <div className="flex h-[40vh] flex-col items-center justify-center gap-4 px-4 text-center text-muted-foreground opacity-60">
                  <div className="rounded-full bg-muted p-4">
                    <Search className="h-6 w-6 text-muted-foreground sm:h-8 sm:w-8" />
                  </div>
                  <div>
                    <p className="text-base font-medium text-foreground sm:text-lg">
                      Nenhum evento encontrado
                    </p>
                    <p className="mt-1 text-[11px] sm:text-sm">
                      Tente ajustar seus termos de busca.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-8">
                  {groupedLogs.map(([dateKey, items]) => (
                    <div key={dateKey}>
                      <div className="mb-4 flex items-center gap-2">
                        <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {formatGroupDate(dateKey)}
                        </span>
                      </div>

                      <ol className="relative space-y-3 border-l-2 border-border pl-8">
                        {items.map((log) => (
                          <TarefaLogItem key={log.id} log={log} />
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-border bg-muted/10 p-3 sm:p-4">
          <span className="text-[10px] font-medium text-muted-foreground sm:text-xs">
            <span className="hidden sm:inline">Mostrando </span>
            {filteredLogs.length} registros
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="h-8 text-xs sm:h-9 sm:text-sm"
          >
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
